import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { form, FormField as SignalFormField, required } from '@angular/forms/signals';

import {
  AppSvgIconComponent,
  OverlayService,
  SignalButton,
  SignalCheckbox,
  SignalFormDirective,
  SignalTextField,
} from '@vallift/ngl';

import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type { DataLibraryCollectionFieldResponse } from '@core/api/workspace/models/dataLibraryCollectionFieldResponse';

import type {
  NumberGridItem,
  FormField,
  FormFieldDataLibraryCollectionRef,
} from '../../../../models/built-form';
import { readNumberGridConfig } from '../../../../models/built-form';

import type { NumberGridColumn } from '@shared/components/number-grid/number-grid';

import { createGridChoice } from '../../../../utils/grid-choice-option';

import {
  LinkCollectionButtonComponent,
  type LinkedCollectionResult,
} from '../../../link-collection-button/link-collection-button';

import {
  CollectionColumnPickerOverlayComponent,
  type CollectionColumnPickerResult,
} from '../../../collection-column-picker-overlay/collection-column-picker-overlay';

import {
  resolveCollectionDropdownItems,
  type CollectionSelectOption,
} from '../../../../utils/resolve-collection-dropdown-items';

import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDragPlaceholder,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';

interface NumberGridConfigModel {
  label: string;
  description: string;
  items: NumberGridItem[];
  columns: NumberGridColumn[];
  required: boolean;
}

interface ColumnOption {
  key: string;
  label: string;
}

function createNumberGridColumn(label = ''): NumberGridColumn {
  const choice = createGridChoice(label);

  return {
    id: choice.id,
    label: choice.label,
  };
}

function createDefaultNumberGridColumns(): NumberGridColumn[] {
  return [createNumberGridColumn('Column 1')];
}

@Component({
  selector: 'app-number-grid-config-view',
  standalone: true,
  imports: [
    SignalFormField,
    SignalFormDirective,
    SignalTextField,
    SignalCheckbox,
    SignalButton,
    AppSvgIconComponent,
    LinkCollectionButtonComponent,
    CdkDropList,
    CdkDragHandle,
    CdkDrag,
    CdkDragPlaceholder,
  ],
  templateUrl: './number-grid-config-view.html',
})
export class NumberGridConfigViewComponent {
  private readonly workspaceApi = inject(WorkspaceAPIService);
  private readonly overlayService = inject(OverlayService);

  readonly field = input.required<FormField>();
  readonly fieldChange = output<FormField>();

  readonly model = signal<NumberGridConfigModel>({
    label: '',
    description: '',
    items: [{ display: 'Item 1' }, { display: 'Item 2' }, { display: 'Item 3' }],
    columns: createDefaultNumberGridColumns(),
    required: false,
  });

  readonly linkedCollection = signal<FormFieldDataLibraryCollectionRef | null>(null);

  readonly isCollectionLinked = computed(() => this.linkedCollection() !== null);

  readonly collectionColumns = signal<ColumnOption[]>([]);
  readonly collectionItems = signal<CollectionSelectOption[]>([]);

  readonly configForm = form(this.model, (schema) => {
    required(schema.label);
  });

  private lastFieldId: string | null = null;
  private lastLoadedCollectionId: string | null = null;
  private suppressEmit = false;

  constructor() {
    // Load field configuration.
    effect(() => {
      const next = this.field();

      if (next.type !== 'number_grid') {
        return;
      }

      if (next.id === this.lastFieldId) {
        return;
      }

      this.lastFieldId = next.id;
      this.suppressEmit = true;

      const config = readNumberGridConfig(next);
      const collection = config.itemsDataLibraryCollection;

      this.linkedCollection.set(collection?.dataLibraryId ? { ...collection } : null);

      this.model.set({
        label: next.label,
        description: next.helpText ?? '',

        items: config.items?.length
          ? config.items.map((item) => ({ ...item }))
          : [{ display: 'Item 1' }, { display: 'Item 2' }, { display: 'Item 3' }],

        columns: config.columns?.length
          ? this.hydrateNumberGridColumns(config.columns)
          : createDefaultNumberGridColumns(),

        required: next.required ?? next.validation?.required ?? false,
      });
    });

    effect(() => {
      const collectionId = this.linkedCollection()?.dataLibraryId?.trim() ?? '';

      if (!collectionId) {
        this.collectionColumns.set([]);
        this.collectionItems.set([]);
        this.lastLoadedCollectionId = null;
        return;
      }

      if (collectionId === this.lastLoadedCollectionId) {
        return;
      }

      this.lastLoadedCollectionId = collectionId;
      this.loadCollectionMetadata(collectionId);
    });

    effect((onCleanup) => {
      const linked = this.linkedCollection();

      const collectionId = linked?.dataLibraryId?.trim() ?? '';
      const displayFieldKey = linked?.displayFieldKey?.trim() ?? '';

      if (!collectionId || !displayFieldKey) {
        this.collectionItems.set([]);
        return;
      }

      const subscription = resolveCollectionDropdownItems(
        this.workspaceApi,
        collectionId,
        displayFieldKey,
      ).subscribe({
        next: (items) => this.collectionItems.set(items),
        error: () => this.collectionItems.set([]),
      });

      onCleanup(() => subscription.unsubscribe());
    });

    effect(() => {
      const draft = this.model();
      const linked = this.linkedCollection();

      if (this.suppressEmit) {
        this.suppressEmit = false;
        return;
      }

      const current = untracked(() => this.field());

      if (!current || current.id !== this.lastFieldId || current.type !== 'number_grid') {
        return;
      }

      const existingConfig = readNumberGridConfig(current);

      const items = linked
        ? undefined
        : draft.items
            .filter((item) => item.display.trim().length > 0)
            .map((item) => ({ ...item }));

      const columns = draft.columns
        .filter((column) => column.label.trim().length > 0)
        .map((column) => ({
          id: column.id,
          label: column.label.trim(),
        }));

      this.fieldChange.emit({
        ...current,
        label: draft.label.trim(),
        helpText: draft.description.trim() || undefined,
        required: draft.required,

        validation: {
          ...current.validation,
          required: draft.required,
        },

        config: {
          ...existingConfig,
          columns,
          items,
          itemsDataLibraryCollection: linked ? { ...linked } : undefined,
        },

        // Row source lives on config.itemsDataLibraryCollection (like other grids).
        dataLibraryCollection: undefined,
      });
    });
  }

  private hydrateNumberGridColumns(raw: unknown): NumberGridColumn[] {
    if (!Array.isArray(raw)) {
      return [];
    }

    return raw.flatMap((rawColumn): NumberGridColumn[] => {
      if (rawColumn === null || typeof rawColumn !== 'object' || Array.isArray(rawColumn)) {
        return [];
      }

      const column = rawColumn as Record<string, unknown>;

      const label = typeof column['label'] === 'string' ? column['label'] : '';

      const existingId =
        typeof column['id'] === 'string' && column['id'].trim()
          ? column['id'].trim()
          : typeof column['key'] === 'string' && column['key'].trim()
            ? column['key'].trim()
            : null;

      const choice = existingId ? { id: existingId, label } : createGridChoice(label);

      return [
        {
          id: choice.id,
          label: choice.label,
        },
      ];
    });
  }

  addItem(): void {
    this.model.update((model) => ({
      ...model,
      items: [...model.items, { display: '' }],
    }));
  }

  removeItem(index: number): void {
    this.model.update((model) =>
      model.items.length <= 1
        ? model
        : {
            ...model,
            items: model.items.filter((_, i) => i !== index),
          },
    );
  }

  onItemDrop(event: CdkDragDrop<NumberGridItem[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    this.model.update((model) => {
      const items = [...model.items];

      moveItemInArray(items, event.previousIndex, event.currentIndex);

      return { ...model, items };
    });
  }

  addColumn(): void {
    this.model.update((model) => ({
      ...model,
      columns: [...model.columns, createNumberGridColumn('')],
    }));
  }

  removeColumn(index: number): void {
    this.model.update((model) =>
      model.columns.length <= 1
        ? model
        : {
            ...model,
            columns: model.columns.filter((_, i) => i !== index),
          },
    );
  }

  onColumnDrop(event: CdkDragDrop<NumberGridColumn[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    this.model.update((model) => {
      const columns = [...model.columns];

      moveItemInArray(columns, event.previousIndex, event.currentIndex);

      return { ...model, columns };
    });
  }

  onCollectionSelected(result: LinkedCollectionResult): void {
    const current = this.field();

    const collection: FormFieldDataLibraryCollectionRef = {
      dataLibraryId: result.id,
      displayFieldKey: result.displayFieldKey,
      displayFieldLabel: result.displayFieldLabel,
      label: result.name,
      key: result.key,
    };

    this.lastLoadedCollectionId = null;
    this.linkedCollection.set(collection);

    this.fieldChange.emit({
      ...current,
      dataLibraryCollection: undefined,
      config: {
        ...readNumberGridConfig(current),
        items: undefined,
        itemsDataLibraryCollection: collection,
      },
    });
  }

  onDisplayFieldKeyChange(value: unknown): void {
    const key = typeof value === 'string' ? value.trim() : '';

    if (!key) {
      return;
    }

    const label = this.collectionColumns().find((column) => column.key === key)?.label ?? key;

    this.linkedCollection.update((linked) =>
      linked
        ? {
            ...linked,
            displayFieldKey: key,
            displayFieldLabel: label,
          }
        : linked,
    );
  }

  changeShowAsColumn(): void {
    const linked = this.linkedCollection();

    if (!linked?.dataLibraryId) {
      return;
    }

    const dialogRef = this.overlayService.openModal(CollectionColumnPickerOverlayComponent, {
      fitContent: true,
      panelClass: ['w-[22rem]', 'max-w-[calc(100%-2rem)]'],
      data: {
        collectionId: linked.dataLibraryId,
        collectionName: linked.label,
        selectedDisplayFieldKey: linked.displayFieldKey,
      },
    });

    (
      dialogRef as {
        closed: import('rxjs').Observable<CollectionColumnPickerResult | null | undefined>;
      }
    ).closed.subscribe((result) => {
      if (!result?.displayFieldKey) {
        return;
      }

      this.linkedCollection.update((current) =>
        current
          ? {
              ...current,
              displayFieldKey: result.displayFieldKey,
              displayFieldLabel: result.displayFieldLabel,
            }
          : current,
      );
    });
  }

  detachLinkedCollection(): void {
    this.unlinkCollectionAndMaterializeItems();
  }

  removeLinkedCollection(): void {
    this.unlinkCollectionAndMaterializeItems();
  }

  private unlinkCollectionAndMaterializeItems(): void {
    const items: NumberGridItem[] = this.collectionItems().map((item) => ({
      id: item.id,
      uniqueKey: item.uniqueKey,
      display: item.display,
    }));

    this.linkedCollection.set(null);
    this.collectionColumns.set([]);
    this.collectionItems.set([]);
    this.lastLoadedCollectionId = null;

    this.model.update((current) => ({
      ...current,
      items: items.length ? items : [{ display: '' }],
    }));
  }

  private loadCollectionMetadata(collectionId: string): void {
    this.workspaceApi.getDataLibraryCollection(collectionId).subscribe({
      next: (response) => {
        const data = response.data;

        if (!data || this.linkedCollection()?.dataLibraryId !== collectionId) {
          return;
        }

        const columns = (data.fields ?? []).map((field: DataLibraryCollectionFieldResponse) => ({
          key: field.key,
          label: field.name || field.key,
        }));

        this.collectionColumns.set(columns);

        const linked = this.linkedCollection();
        const key = linked?.displayFieldKey?.trim();

        if (key && !linked?.displayFieldLabel) {
          const label = columns.find((column) => column.key === key)?.label;

          if (label) {
            this.linkedCollection.update((current) =>
              current ? { ...current, displayFieldLabel: label } : current,
            );
          }
        }
      },

      error: () => this.collectionColumns.set([]),
    });
  }
}
